import { useState } from "react";
import { Button } from "../../../../components/Button";
import { setCatalogPublication } from "../../../../services/calendar";
import type { CalendarEventResponse } from "../../../../types/api";
import "./Itinerary.css";

interface CatalogPublicationToggleProps {
  /**
   * Only the three fields this toggle actually reads, so it can be used somewhere that has just
   * published a tournament and holds its id rather than a fetched event — the Build Itinerary
   * share step. Passing a whole `CalendarEventResponse` still satisfies it.
   */
  event: Pick<CalendarEventResponse, 'id' | 'eventType' | 'isShareable'>;
  /** Called with the new state so the parent can refresh its copy of the event. */
  onChanged?: (isShareable: boolean) => void;
}

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/**
 * Publishes a tournament to the shared itinerary catalog, or withdraws it (REQ-01 / REQ-02).
 *
 * <p><b>This is the only writer of `isShareable`</b>, which is the single flag bounding the
 * catalog's deliberate cross-organization read. It therefore lives inside the `system_admin`
 * distribution panel rather than on the ordinary event editor: a coach publishing their own
 * organization's tournament would put it in front of every other program on the platform.</p>
 *
 * <p>Publishing is refused server-side for a non-tournament, a draft, a distributed copy and
 * another org's event; the messages are surfaced verbatim rather than pre-empted here, so the
 * server stays the one place those rules live.</p>
 */
export function CatalogPublicationToggle({ event, onChanged }: CatalogPublicationToggleProps) {
  const [isShareable, setIsShareable] = useState(event.isShareable);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Only a tournament can ever be published, so there is nothing to offer on anything else.
  if (event.eventType !== "Tournament") return null;

  const toggle = async () => {
    const next = !isShareable;
    setBusy(true);
    setError("");
    try {
      const result = await setCatalogPublication(event.id, next);
      setIsShareable(result.isShareable);
      onChanged?.(result.isShareable);
    } catch (err) {
      setError(
        errorMessage(
          err,
          next
            ? "Couldn't publish this tournament. Try again."
            : "Couldn't withdraw this tournament. Try again.",
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="event-view__section itin-publish" data-testid="catalog-publication">
      <div className="itin-publish__head">🌐 Shared itinerary catalog</div>

      <p className="itin-publish__state" data-testid="catalog-publication-state">
        {isShareable
          ? "Published. Any program can find this tournament by date and add it to its own calendar."
          : "Not published. Only organizations you distribute it to will see it."}
      </p>

      <Button
        type="button"
        variant={isShareable ? "secondary" : "primary"}
        onClick={() => void toggle()}
        disabled={busy}
        data-testid="catalog-publication-toggle"
      >
        {busy
          ? isShareable
            ? "Withdrawing…"
            : "Publishing…"
          : isShareable
            ? "Withdraw from the catalog"
            : "Publish to the catalog"}
      </Button>

      {error && (
        <div
          className="event-editor__error"
          role="alert"
          data-testid="catalog-publication-error"
        >
          {error}
        </div>
      )}

      <div className="event-editor__hint">
        Withdrawing only affects future browsing. Programs that already added this tournament keep
        their copy, because it is their own event by then.
      </div>
    </div>
  );
}
